import { useCallback, useMemo } from 'react';
import { getItemKey, itemSelectionToStringSet } from "./itemUtils.js";
/**
 * Spectrum collection components treat keys as strings if the `key` prop is
 * explicitly set on `Item` elements. Since we do this in `useRenderNormalizedItem`,
 * we need to ensure that keys are strings in order for selection to work. We
 * then need to convert back to the original key types in the onChange handler.
 * This hook encapsulates converting to and from strings so that keys can match
 * the original key type.
 * @param normalizedItems The normalized items to select from.
 * @param selectedKey The currently selected key in the collection.
 * @param defaultSelectedKey The initial selected key in the collection.
 * @param disabledKeys The currently disabled keys in the collection.
 * @param onChange Handler that is called when the selection changes.
 * @returns UseStringifiedSelectionResult with stringified key sets and string
 * key selection change handler.
 */
export function useStringifiedSelection(_ref) {
  var normalizedItems = _ref.normalizedItems,
    defaultSelectedKey = _ref.defaultSelectedKey,
    selectedKey = _ref.selectedKey,
    disabledKeys = _ref.disabledKeys,
    onChange = _ref.onChange;
  var selectedStringKey = useMemo(() => selectedKey == null ? selectedKey : String(selectedKey), [selectedKey]);
  var defaultSelectedStringKey = useMemo(() => defaultSelectedKey == null ? defaultSelectedKey : String(defaultSelectedKey), [defaultSelectedKey]);
  var disabledStringKeys = useMemo(() => itemSelectionToStringSet(disabledKeys), [disabledKeys]);
  var onStringSelectionChange = useCallback(key => {
    var _getItemKey;
    if (onChange == null) {
      return;
    }

    // The `key` arg will always be a string due to us setting the `Item` key
    // prop in `renderItem`. We need to find the matching item to determine
    // the actual key.
    var selectedItem = normalizedItems.find(item => String(getItemKey(item)) === key);
    var actualKey = (_getItemKey = getItemKey(selectedItem)) !== null && _getItemKey !== void 0 ? _getItemKey : key;
    onChange(actualKey);
  }, [normalizedItems, onChange]);
  return {
    selectedStringKey,
    defaultSelectedStringKey,
    disabledStringKeys,
    onStringSelectionChange
  };
}
export default useStringifiedSelection;
//# sourceMappingURL=useStringifiedSelection.js.map